import { stringify } from "querystring";

import {
  getAccountNumberInvalidErrorKey,
  getBankAccountFields,
  OrganizationCountry,
  OrganizationRoutes,
} from "@simplyk/common";

import { Translate } from "../../../../../common/hooks/useTranslate";

import { DashboardLink } from "@/dashboard-design-system/Link";

export const updateOrganizationInformationQueryParam = "updateOrganizationInformation";

const bankSettingLink = `${OrganizationRoutes.Settings}?${stringify({ [updateOrganizationInformationQueryParam]: true })}`;

export const getStripeExternalAccountError = ({
  errorCode,
  routingNumber,
  t,
  organizationCountry,
  transitNumber,
  institutionNumber,
  sortCode,
  bsb,
}: {
  errorCode: "account_number_invalid" | "routing_number_invalid" | "bank_account_unusable" | string;
  routingNumber?: string | null;
  t: Translate;
  organizationCountry: OrganizationCountry | undefined;
  transitNumber?: string;
  institutionNumber?: string;
  sortCode?: string | null;
  bsb?: string | null;
}): React.ReactNode => {
  const bankAccountField = getBankAccountFields(organizationCountry);

  if (errorCode === "routing_number_invalid") {
    if (bankAccountField === "transitAndInstitution") {
      return getCanadianRoutingNumberInvalidError(t, transitNumber, institutionNumber);
    } else if (bankAccountField === "sortCode") {
      return getGreatBritainRoutingNumberInvalidError(t, sortCode);
    } else if (bankAccountField === "bsb") {
      return getAustralianBsbInvalidError(t, bsb);
    } else {
      return getUsRoutingNumberInvalidError(t, routingNumber);
    }
  }

  if (errorCode === "account_number_invalid") {
    return t("dashboard", getAccountNumberInvalidErrorKey(organizationCountry));
  }

  if (errorCode === "bank_account_unusable") {
    return t("dashboard", "bankAccountStep.error.bankAccountUnusable");
  }

  return (
    <>
      {t("dashboard", "bankAccountStep.error.unknownError")}
      <DashboardLink href={bankSettingLink} vibe="danger" underline="always">
        {t("dashboard", "bankAccountStep.error.settings.link")}
      </DashboardLink>
    </>
  );
};

const getCanadianRoutingNumberInvalidError = (t: Translate, transitNumber?: string, institutionNumber?: string) => {
  if (transitNumber && !isTransitNumberValid(transitNumber)) {
    return t("dashboard", "bankAccountStep.error.routingNumberInvalid_ca_transitNumber");
  }

  if (institutionNumber && !isInstitutionNumberValid(institutionNumber)) {
    return t("dashboard", "bankAccountStep.error.routingNumberInvalid_ca_institutionNumber");
  }

  return (
    <>
      {t("dashboard", "bankAccountStep.error.routingNumberInvalid")}
      <DashboardLink href={bankSettingLink} vibe="danger" underline="always">
        {t("dashboard", "bankAccountStep.error.settings.link")}
      </DashboardLink>
    </>
  );
};

const getGreatBritainRoutingNumberInvalidError = (t: Translate, sortCode?: string | null) => {
  if (sortCode && !isSortCodeValid(sortCode)) {
    return t("dashboard", "bankAccountStep.error.routingNumberInvalid_gb_sortCode");
  }
};

const getAustralianBsbInvalidError = (t: Translate, bsb?: string | null) => {
  if (bsb && !isBsbValid(bsb)) {
    return t("dashboard", "createBankAccount.bsbInvalidLength");
  }
};

export const getUsRoutingNumberInvalidError = (t: Translate, routingNumber?: string | null) => {
  if (routingNumber && !isValidUSRoutingNumber(routingNumber)) {
    return t("dashboard", "bankAccountStep.error.routingNumberInvalid_us_format");
  }

  if (routingNumber && !isUsAlgoValid(routingNumber)) {
    return t("dashboard", "bankAccountStep.error.routingNumberInvalid_us_algo");
  }

  return (
    <>
      {t("dashboard", "bankAccountStep.error.routingNumberInvalid")}
      <DashboardLink href={bankSettingLink} vibe="danger" underline="always">
        {t("dashboard", "bankAccountStep.error.settings.link")}
      </DashboardLink>
    </>
  );
};

export const isValidUSRoutingNumber = (routingNumber: string) => {
  // must be 9 digits and only digits
  return routingNumber.length === 9 && /^\d+$/.test(routingNumber);
};

export const isUsAlgoValid = (routingNumber: string) => {
  if (!isValidUSRoutingNumber(routingNumber)) {
    return false;
  }

  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(routingNumber[i], 10) * weights[i];
  }
  if (sum <= 0) {
    return false;
  }

  return sum % 10 === 0;
};

export const isTransitNumberValid = (transitNumber: string) => {
  return transitNumber.length === 5 && /^\d+$/.test(transitNumber);
};

export const isInstitutionNumberValid = (institutionNumber: string) => {
  return institutionNumber.length === 3 && /^\d+$/.test(institutionNumber);
};

export const isSortCodeValid = (sortCode: string) => {
  return sortCode.length === 6 && /^\d+$/.test(sortCode);
};

export const isBsbValid = (bsb: string) => {
  return bsb.length === 6 && /^\d+$/.test(bsb);
};
